import { createBrowserRouter } from 'react-router'

import { RouteErrorFallback } from './RouteErrorFallback'
import { RootLayout } from '@/app/layouts/RootLayout'
import { RedirectIfAuthenticated, RequireAuth } from '@/features/auth/components/guards'
import { PageLoader } from '@/shared/components/PageLoader'

/**
 * Every route below is code-split: `lazy` makes the bundler emit a separate
 * chunk per page, downloaded the first time the route is visited. Pages
 * that share feature modules automatically share a chunk.
 */
export const router = createBrowserRouter([
  {
    path: '/',
    Component: RootLayout,
    ErrorBoundary: RouteErrorFallback,
    HydrateFallback: PageLoader,
    children: [
      // Auth pages that make no sense once signed in.
      {
        Component: RedirectIfAuthenticated,
        children: [
          {
            path: 'login',
            lazy: () =>
              import('@/features/auth/pages/LoginPage').then((m) => ({ Component: m.LoginPage })),
          },
          {
            path: 'register',
            lazy: () =>
              import('@/features/auth/pages/RegisterPage').then((m) => ({
                Component: m.RegisterPage,
              })),
          },
          {
            path: 'forgot-password',
            lazy: () =>
              import('@/features/auth/pages/ForgotPasswordPage').then((m) => ({
                Component: m.ForgotPasswordPage,
              })),
          },
        ],
      },

      // Public landing page shown to signed-out visitors of the site root,
      // and the marketing pages that used to be static HTML in public/.
      {
        path: 'welcome',
        lazy: () =>
          import('@/features/landing/pages/LandingPage').then((m) => ({
            Component: m.LandingPage,
          })),
      },
      {
        path: 'about',
        lazy: () =>
          import('@/features/landing/pages/AboutPage').then((m) => ({ Component: m.AboutPage })),
      },
      {
        path: 'features',
        lazy: () =>
          import('@/features/landing/pages/FeaturesPage').then((m) => ({
            Component: m.FeaturesPage,
          })),
      },
      {
        path: 'creators',
        lazy: () =>
          import('@/features/landing/pages/CreatorsPage').then((m) => ({
            Component: m.CreatorsPage,
          })),
      },
      {
        path: 'contact',
        lazy: () =>
          import('@/features/landing/pages/ContactPage').then((m) => ({
            Component: m.ContactPage,
          })),
      },
      {
        path: 'download',
        lazy: () =>
          import('@/features/landing/pages/DownloadPage').then((m) => ({
            Component: m.DownloadPage,
          })),
      },

      // Email link targets - must match the URLs the backend emails point to.
      {
        path: 'validate-email',
        lazy: () =>
          import('@/features/auth/pages/ValidateEmailPage').then((m) => ({
            Component: m.ValidateEmailPage,
          })),
      },
      {
        path: 'reset-password',
        lazy: () =>
          import('@/features/auth/pages/ResetPasswordPage').then((m) => ({
            Component: m.ResetPasswordPage,
          })),
      },

      // Signed-in area - the entire app, like the mobile clients.
      {
        Component: RequireAuth,
        children: [
          {
            index: true,
            lazy: () =>
              import('@/features/feed/pages/FeedPage').then((m) => ({ Component: m.FeedPage })),
          },
          {
            // The feed switch's Map pill: every Business page location.
            path: 'map',
            lazy: () =>
              import('@/features/map/pages/MapPage').then((m) => ({ Component: m.MapPage })),
          },
          {
            // One account's Snaps, played full screen (opened from the
            // Snaps row of My circle, or a profile's Snaps tab).
            path: 'snaps/:userId',
            lazy: () =>
              import('@/features/snaps/pages/SnapPlayerPage').then((m) => ({
                Component: m.SnapPlayerPage,
              })),
          },
          {
            // A point of interest, opened from the Map (markers, lists, search).
            path: 'poi/:id',
            lazy: () =>
              import('@/features/pois/pages/PoiPage').then((m) => ({ Component: m.PoiPage })),
          },
          {
            path: 'search',
            lazy: () =>
              import('@/features/search/pages/SearchPage').then((m) => ({
                Component: m.SearchPage,
              })),
          },
          {
            path: 'messages/:conversationId?',
            lazy: () =>
              import('@/features/messaging/pages/MessagesPage').then((m) => ({
                Component: m.MessagesPage,
              })),
          },
          {
            path: 'notifications',
            lazy: () =>
              import('@/features/notifications/pages/NotificationsPage').then((m) => ({
                Component: m.NotificationsPage,
              })),
          },
          {
            path: 'notifications/settings',
            lazy: () =>
              import('@/features/notifications/pages/NotificationSettingsPage').then((m) => ({
                Component: m.NotificationSettingsPage,
              })),
          },
          {
            path: 'create',
            lazy: () =>
              import('@/features/upload/pages/CreatePostPage').then((m) => ({
                Component: m.CreatePostPage,
              })),
          },
          {
            path: 'communities',
            lazy: () =>
              import('@/features/communities/pages/CommunitiesHubPage').then((m) => ({
                Component: m.CommunitiesHubPage,
              })),
          },
          {
            path: 'communities/new',
            lazy: () =>
              import('@/features/communities/pages/CreateCommunityPage').then((m) => ({
                Component: m.CreateCommunityPage,
              })),
          },
          {
            path: 'communities/favourites',
            lazy: () =>
              import('@/features/communities/pages/CommunityListPage').then((m) => ({
                Component: m.FavouriteCommunitiesPage,
              })),
          },
          {
            path: 'communities/featured',
            lazy: () =>
              import('@/features/communities/pages/CommunityListPage').then((m) => ({
                Component: m.FeaturedCommunitiesPage,
              })),
          },
          {
            path: 'communities/user/:userId',
            lazy: () =>
              import('@/features/communities/pages/CommunityListPage').then((m) => ({
                Component: m.UserCommunitiesPage,
              })),
          },
          {
            path: 'community/:slug/edit',
            lazy: () =>
              import('@/features/communities/pages/CreateCommunityPage').then((m) => ({
                Component: m.EditCommunityPage,
              })),
          },
          {
            path: 'community/:slug/members',
            lazy: () =>
              import('@/features/communities/pages/CommunityMembersPage').then((m) => ({
                Component: m.CommunityMembersPage,
              })),
          },
          {
            path: 'profile/:userId',
            lazy: () =>
              import('@/features/profile/pages/ProfilePage').then((m) => ({
                Component: m.ProfilePage,
              })),
          },
          {
            path: 'profile/:userId/:listKind',
            lazy: () =>
              import('@/features/profile/pages/FollowListPage').then((m) => ({
                Component: m.FollowListPage,
              })),
          },
          {
            // Anyone's achievement badges (the owner also sees the progress).
            path: 'badges/:userId',
            lazy: () =>
              import('@/features/badges/pages/BadgesPage').then((m) => ({
                Component: m.BadgesPage,
              })),
          },
          {
            // The own visited places (the Travel section's button), searchable and paged.
            path: 'travel/places',
            lazy: () =>
              import('@/features/travel/pages/TravelPlacesPage').then((m) => ({
                Component: m.TravelPlacesPage,
              })),
          },
          {
            path: 'friends',
            lazy: () =>
              import('@/features/profile/pages/FriendsPage').then((m) => ({
                Component: m.FriendsPage,
              })),
          },
          {
            path: 'about-edit',
            lazy: () =>
              import('@/features/profile/pages/AboutEditPage').then((m) => ({
                Component: m.AboutEditPage,
              })),
          },
          {
            path: 'edit-profile',
            lazy: () =>
              import('@/features/profile/pages/EditProfilePage').then((m) => ({
                Component: m.EditProfilePage,
              })),
          },
          {
            path: 'pages/new',
            lazy: () =>
              import('@/features/profile/pages/CreatePagePage').then((m) => ({
                Component: m.CreatePagePage,
              })),
          },
          {
            path: 'pages/:username/edit',
            lazy: () =>
              import('@/features/profile/pages/CreatePagePage').then((m) => ({
                Component: m.EditPagePage,
              })),
          },
          {
            // A Business page opened through one of its locations (a Map pin,
            // a travel place, a search hit): the same page in location mode.
            path: 'pages/by-location/:locationId',
            lazy: () =>
              import('@/features/profile/pages/PageDetailsPage').then((m) => ({
                Component: m.PageDetailsPage,
              })),
          },
          {
            // The other locations of the Business page opened through this
            // one; a card swaps the location (replace) and leaves the list.
            path: 'pages/by-location/:locationId/others',
            lazy: () =>
              import('@/features/profile/pages/PageLocationsPage').then((m) => ({
                Component: m.PageLocationsPage,
              })),
          },
          {
            // A business location's ratings with their comments.
            path: 'pages/by-location/:locationId/ratings',
            lazy: () =>
              import('@/features/profile/pages/LocationRatingsPage').then((m) => ({
                Component: m.LocationRatingsPage,
              })),
          },
          {
            path: 'pages/:username/admins',
            lazy: () =>
              import('@/features/profile/pages/PageAdminsPage').then((m) => ({
                Component: m.PageAdminsPage,
              })),
          },
          {
            // The locations of a Business page opened by handle (no location
            // is open, so none is left out).
            path: 'pages/:username/locations',
            lazy: () =>
              import('@/features/profile/pages/PageLocationsPage').then((m) => ({
                Component: m.PageLocationsPage,
              })),
          },
          {
            path: 'ranking',
            lazy: () =>
              import('@/features/profile/pages/RankingPage').then((m) => ({
                Component: m.RankingPage,
              })),
          },
          {
            path: 'analytics',
            lazy: () =>
              import('@/features/profile/pages/AnalyticsPage').then((m) => ({
                Component: m.AnalyticsPage,
              })),
          },
          {
            path: 'blocked',
            lazy: () =>
              import('@/features/profile/pages/BlockedUsersPage').then((m) => ({
                Component: m.BlockedUsersPage,
              })),
          },
          {
            // Hyphenated so no page handle (a-z0-9_) can shadow it.
            path: 'followed-pages',
            lazy: () =>
              import('@/features/profile/pages/FollowedPagesPage').then((m) => ({
                Component: m.FollowedPagesPage,
              })),
          },
          {
            path: 'wallet/transactions',
            lazy: () =>
              import('@/features/profile/pages/WalletTransactionsPage').then((m) => ({
                Component: m.WalletTransactionsPage,
              })),
          },
          {
            path: 'premium',
            lazy: () =>
              import('@/features/profile/pages/PremiumPage').then((m) => ({
                Component: m.PremiumPage,
              })),
          },
          {
            path: 'topup',
            lazy: () =>
              import('@/features/profile/pages/TopUpPage').then((m) => ({
                Component: m.TopUpPage,
              })),
          },
          {
            path: 'payments/return',
            lazy: () =>
              import('@/features/profile/pages/PaymentsReturnPage').then((m) => ({
                Component: m.PaymentsReturnPage,
              })),
          },
          {
            path: 'interests',
            lazy: () =>
              import('@/features/settings/pages/InterestsPage').then((m) => ({
                Component: m.InterestsPage,
              })),
          },
          {
            path: 'settings',
            lazy: () =>
              import('@/features/settings/pages/SettingsPage').then((m) => ({
                Component: m.SettingsPage,
              })),
          },
          {
            path: 'settings/change-email',
            lazy: () =>
              import('@/features/settings/pages/ChangeEmailPage').then((m) => ({
                Component: m.ChangeEmailPage,
              })),
          },
          {
            path: 'settings/change-username',
            lazy: () =>
              import('@/features/settings/pages/ChangeUsernamePage').then((m) => ({
                Component: m.ChangeUsernamePage,
              })),
          },
          {
            path: 'settings/deleted-posts',
            lazy: () =>
              import('@/features/settings/pages/DeletedPostsPage').then((m) => ({
                Component: m.DeletedPostsPage,
              })),
          },
          {
            path: 'settings/delete-account',
            lazy: () =>
              import('@/features/settings/pages/DeleteAccountPage').then((m) => ({
                Component: m.DeleteAccountPage,
              })),
          },
          {
            path: 'support',
            lazy: () =>
              import('@/features/support/pages/SupportPage').then((m) => ({
                Component: m.SupportPage,
              })),
          },
          {
            path: 'support/:ticketId',
            lazy: () =>
              import('@/features/support/pages/SupportTicketPage').then((m) => ({
                Component: m.SupportTicketPage,
              })),
          },
        ],
      },

      // Shared post links: on phones the gate swaps the signed-in post page
      // for the open-in-app/store hand-off, so a shared link never strands a
      // recipient on the web login. Desktop falls through to RequireAuth.
      {
        path: 'post/:postId',
        lazy: () => import('./OpenInAppPage').then((m) => ({ Component: m.PostAppGate })),
        children: [
          {
            Component: RequireAuth,
            children: [
              {
                index: true,
                lazy: () =>
                  import('@/features/feed/pages/PostPage').then((m) => ({ Component: m.PostPage })),
              },
            ],
          },
        ],
      },

      // Shared page links: phones get the open-in-app/store hand-off,
      // desktop falls through to the signed-in web page details. Lives
      // outside RequireAuth so a signed-out phone never sees the login;
      // /pages/new, /pages/:username/edit and /pages/:username/admins stay
      // in the signed-in area (static segments outrank the param, so
      // /pages/new still wins).
      {
        path: 'pages/:username',
        lazy: () => import('./OpenInAppPage').then((m) => ({ Component: m.PageAppGate })),
        children: [
          {
            Component: RequireAuth,
            children: [
              {
                index: true,
                lazy: () =>
                  import('@/features/profile/pages/PageDetailsPage').then((m) => ({
                    Component: m.PageDetailsPage,
                  })),
              },
            ],
          },
        ],
      },

      // Shared community links: phones get the open-in-app/store hand-off,
      // desktop falls through to the signed-in web community page.
      {
        path: 'community/:slug',
        lazy: () => import('./OpenInAppPage').then((m) => ({ Component: m.CommunityAppGate })),
        children: [
          {
            Component: RequireAuth,
            children: [
              {
                index: true,
                lazy: () =>
                  import('@/features/communities/pages/CommunityPage').then((m) => ({
                    Component: m.CommunityPage,
                  })),
              },
            ],
          },
        ],
      },

      // Store hand-off for QR codes and print: /get picks the store by device.
      // Declared before :handle, which would otherwise swallow the segment.
      {
        path: 'get',
        lazy: () => import('./GetAppPage').then((m) => ({ Component: m.GetAppPage })),
      },

      // Shared profile links (/@username): phones with the app installed are
      // routed into it by the OS; everyone else gets the open-in-app page.
      // React Router params must span a whole segment, so this catches every
      // unknown single-segment path and the page 404s the non-@ ones itself.
      {
        path: ':handle',
        lazy: () => import('./OpenInAppPage').then((m) => ({ Component: m.OpenInAppPage })),
      },

      {
        path: '*',
        lazy: () => import('./NotFoundPage').then((m) => ({ Component: m.NotFoundPage })),
      },
    ],
  },
])
